import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import EChart from 'components/Charts/ECharts/EChart.component';
import './AnalyticsDashboard.scss';

// KPI Card Component
const KPICard = ({ title, value, subtitle, trend, trendValue, icon, color }) => (
  <div className="kpi-card" style={{ '--accent-color': color }}>
    <div className="kpi-card__icon">
      <span className="material-icons">{icon}</span>
    </div>
    <div className="kpi-card__content">
      <div className="kpi-card__title">{title}</div>
      <div className="kpi-card__value">{value}</div>
      <div className="kpi-card__footer">
        <span className={`kpi-card__trend kpi-card__trend--${trend}`}>
          <span className="material-icons">
            {trend === 'up' ? 'trending_up' : trend === 'down' ? 'trending_down' : 'trending_flat'}
          </span>
          {trendValue}
        </span>
        <span className="kpi-card__subtitle">{subtitle}</span>
      </div>
    </div>
  </div>
);

// Chart Card Component
const ChartCard = ({ title, subtitle, children, className = '' }) => (
  <div className={`chart-card ${className}`}>
    <div className="chart-card__header">
      <div className="chart-card__title">{title}</div>
      {subtitle && <div className="chart-card__subtitle">{subtitle}</div>}
    </div>
    <div className="chart-card__body">
      {children}
    </div>
  </div>
);

const AnalyticsDashboard = ({ currentUserData }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');

  // Sample KPI Data
  const kpiData = [
    {
      title: 'Total Research',
      value: '2,847',
      subtitle: 'vs last month',
      trend: 'up',
      trendValue: '+12.5%',
      icon: 'search',
      color: '#6366f1'
    },
    {
      title: 'Reports Generated',
      value: '1,234',
      subtitle: 'vs last month',
      trend: 'up',
      trendValue: '+8.2%',
      icon: 'description',
      color: '#10b981'
    },
    {
      title: 'Active Users',
      value: '892',
      subtitle: 'vs last month',
      trend: 'down',
      trendValue: '-2.1%',
      icon: 'people',
      color: '#f59e0b'
    },
    {
      title: 'API Calls',
      value: '45.2K',
      subtitle: 'vs last month',
      trend: 'up',
      trendValue: '+23.8%',
      icon: 'api',
      color: '#ef4444'
    }
  ];

  // Line Chart - Research Trend
  const researchTrendOption = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(17, 24, 39, 0.95)',
      borderColor: 'rgba(75, 85, 99, 0.3)',
      textStyle: { color: '#f3f4f6' }
    },
    legend: {
      data: ['Research', 'Reports', 'Deep Dive'],
      top: 0,
      textStyle: { color: '#6b7280' }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      axisLine: { lineStyle: { color: '#374151' } },
      axisLabel: { color: '#9ca3af' }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: 'rgba(75, 85, 99, 0.2)' } },
      axisLabel: { color: '#9ca3af' }
    },
    series: [
      {
        name: 'Research',
        type: 'line',
        smooth: true,
        data: [120, 182, 191, 234, 290, 330, 310, 345, 378, 420, 456, 520],
        lineStyle: { width: 3, color: '#6366f1' },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(99, 102, 241, 0.3)' },
              { offset: 1, color: 'rgba(99, 102, 241, 0.05)' }
            ]
          }
        },
        itemStyle: { color: '#6366f1' }
      },
      {
        name: 'Reports',
        type: 'line',
        smooth: true,
        data: [80, 120, 140, 160, 180, 210, 195, 220, 245, 280, 310, 350],
        lineStyle: { width: 3, color: '#10b981' },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(16, 185, 129, 0.3)' },
              { offset: 1, color: 'rgba(16, 185, 129, 0.05)' }
            ]
          }
        },
        itemStyle: { color: '#10b981' }
      },
      {
        name: 'Deep Dive',
        type: 'line',
        smooth: true,
        data: [40, 55, 70, 85, 95, 110, 105, 125, 140, 160, 180, 210],
        lineStyle: { width: 3, color: '#f59e0b' },
        itemStyle: { color: '#f59e0b' }
      }
    ]
  }), []);

  // Donut Chart - Report Types
  const reportTypesOption = useMemo(() => ({
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(17, 24, 39, 0.95)',
      borderColor: 'rgba(75, 85, 99, 0.3)',
      textStyle: { color: '#f3f4f6' },
      formatter: '{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      right: '5%',
      top: 'center',
      textStyle: { color: '#9ca3af' }
    },
    series: [
      {
        name: 'Report Types',
        type: 'pie',
        radius: ['45%', '70%'],
        center: ['35%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#1f2937',
          borderWidth: 2
        },
        label: { show: false },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold',
            color: '#f3f4f6'
          }
        },
        labelLine: { show: false },
        data: [
          { value: 435, name: 'Growth Analysis', itemStyle: { color: '#6366f1' } },
          { value: 310, name: 'TAM Analysis', itemStyle: { color: '#10b981' } },
          { value: 234, name: 'Company Report', itemStyle: { color: '#f59e0b' } },
          { value: 155, name: 'In-Depth Analysis', itemStyle: { color: '#ef4444' } },
          { value: 100, name: 'Techno-Economic', itemStyle: { color: '#8b5cf6' } }
        ]
      }
    ]
  }), []);

  // Bar Chart - Top Features
  const topFeaturesOption = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(17, 24, 39, 0.95)',
      borderColor: 'rgba(75, 85, 99, 0.3)',
      textStyle: { color: '#f3f4f6' }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: 'rgba(75, 85, 99, 0.2)' } },
      axisLabel: { color: '#9ca3af' }
    },
    yAxis: {
      type: 'category',
      data: ['Explore Charts', 'Executive Summary', 'Presentation', 'LinkedIn Post', 'Share', 'Deep Dive'],
      axisLine: { lineStyle: { color: '#374151' } },
      axisLabel: { color: '#9ca3af' }
    },
    series: [
      {
        name: 'Usage',
        type: 'bar',
        data: [
          { value: 892, itemStyle: { color: '#6366f1' } },
          { value: 756, itemStyle: { color: '#10b981' } },
          { value: 634, itemStyle: { color: '#f59e0b' } },
          { value: 512, itemStyle: { color: '#ef4444' } },
          { value: 423, itemStyle: { color: '#8b5cf6' } },
          { value: 345, itemStyle: { color: '#06b6d4' } }
        ],
        barWidth: '60%',
        itemStyle: {
          borderRadius: [0, 6, 6, 0]
        }
      }
    ]
  }), []);

  // Radar Chart - Performance Metrics
  const performanceRadarOption = useMemo(() => ({
    tooltip: {
      backgroundColor: 'rgba(17, 24, 39, 0.95)',
      borderColor: 'rgba(75, 85, 99, 0.3)',
      textStyle: { color: '#f3f4f6' }
    },
    legend: {
      data: ['Current', 'Previous'],
      bottom: 0,
      textStyle: { color: '#9ca3af' }
    },
    radar: {
      indicator: [
        { name: 'Accuracy', max: 100 },
        { name: 'Speed', max: 100 },
        { name: 'Relevance', max: 100 },
        { name: 'Coverage', max: 100 },
        { name: 'User Satisfaction', max: 100 }
      ],
      center: ['50%', '45%'],
      radius: '65%',
      axisName: { color: '#9ca3af' },
      splitLine: { lineStyle: { color: 'rgba(75, 85, 99, 0.3)' } },
      splitArea: { show: false },
      axisLine: { lineStyle: { color: 'rgba(75, 85, 99, 0.3)' } }
    },
    series: [
      {
        type: 'radar',
        data: [
          {
            value: [92, 88, 95, 85, 90],
            name: 'Current',
            areaStyle: { color: 'rgba(99, 102, 241, 0.3)' },
            lineStyle: { color: '#6366f1', width: 2 },
            itemStyle: { color: '#6366f1' }
          },
          {
            value: [85, 82, 88, 78, 84],
            name: 'Previous',
            areaStyle: { color: 'rgba(156, 163, 175, 0.2)' },
            lineStyle: { color: '#9ca3af', width: 2, type: 'dashed' },
            itemStyle: { color: '#9ca3af' }
          }
        ]
      }
    ]
  }), []);

  return (
    <div className="analytics-dashboard">
      {/* Header with KPIs */}
      <div className="analytics-dashboard__header">
        <div className="analytics-dashboard__header-left">
          <h1 className="analytics-dashboard__title">Analytics Dashboard</h1>
          <p className="analytics-dashboard__subtitle">
            Overview of your research and report metrics
          </p>
        </div>
        <div className="analytics-dashboard__header-right">
          <div className="period-selector">
            {['daily', 'weekly', 'monthly', 'yearly'].map((period) => (
              <button
                key={period}
                className={`period-selector__btn ${selectedPeriod === period ? 'period-selector__btn--active' : ''}`}
                onClick={() => setSelectedPeriod(period)}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI Cards - All 4 in single row */}
      <div className="analytics-dashboard__kpis">
        {kpiData.map((kpi, index) => (
          <KPICard key={index} {...kpi} />
        ))}
      </div>

      {/* Charts Grid */}
      <div className="analytics-dashboard__charts">
        {/* Main Chart - Full Width */}
        <ChartCard title="Research Trend" subtitle="Monthly activity overview" className="chart-card--full">
          <EChart
            option={researchTrendOption}
            style={{ width: '100%', height: '320px' }}
          />
        </ChartCard>

        {/* Two Column Charts */}
        <ChartCard title="Report Distribution" subtitle="By analysis type">
          <EChart
            option={reportTypesOption}
            style={{ width: '100%', height: '280px' }}
          />
        </ChartCard>

        <ChartCard title="Feature Usage" subtitle="Most popular features">
          <EChart
            option={topFeaturesOption}
            style={{ width: '100%', height: '280px' }}
          />
        </ChartCard>

        {/* Performance Radar */}
        <ChartCard title="Performance Metrics" subtitle="Current vs Previous Period" className="chart-card--half">
          <EChart
            option={performanceRadarOption}
            style={{ width: '100%', height: '280px' }}
          />
        </ChartCard>
      </div>
    </div>
  );
};

const mapStateToProps = (store) => {
  const { rootReducers } = store;
  const { userDataReducers } = rootReducers;
  return {
    currentUserData: userDataReducers.currentUserData,
  };
};

export default connect(mapStateToProps)(AnalyticsDashboard);
